import * as openpgp from "openpgp";
import { IDecryptedKeys } from "./decryptKeys";
import {
  getCompanyPublicKeyBase64,
  peekActiveCompanyKey,
} from "./encryptForCompany";
import { getCompanyPrivateKeys } from "./companyContentKeys";
import { readKeyMaterial } from "./readPublicKey";

/**
 * Klucze przekazywane wtyczce (KFJ.Notaup.Plugin) przy nawiązaniu połączenia.
 *
 * Wtyczka (EditorService.HandleRequest → OpenEditorRequest) odszyfrowuje tym kluczem UserKey
 * dokumentu przy jego otwieraniu. Od F3 klucze obiektów są owinięte kluczem FIRMY
 * (utils/encryptForCompany). Konto bez wydanego grantu (D2 — może istnieć bez firmy, albo
 * czeka na grant) nie ma czym się połączyć poza kluczem intermediate — profile, którymi
 * to kiedyś łatano, zniknęły razem z resztą modelu (F5).
 *
 * ZOSTAJE DO ZROBIENIA (rozstrzygnięcie 17 docs/model-kluczy-firmowych.md): wtyczka ma
 * dostawać klucz POJEDYNCZEGO OBIEKTU przy otwarciu dokumentu, a nie klucz kontekstu.
 * Serwer wtyczki (https://localhost:8883) nasłuchuje BEZ autoryzacji i z odbijanym Origin,
 * więc klucz prywatny całej kancelarii nie powinien tam trafiać ani chwili dłużej, niż to
 * konieczne. Pełne przejście wymaga zmiany PROTOKOŁU: `IOpenEditor` (api/pluginService)
 * musiałby nieść materiał klucza otwieranego dokumentu, a wtyczka używać go zamiast klucza
 * z `/api/connect` — to zmiana po stronie C# wtyczki, poza zakresem tego strumienia.
 */
export interface IPluginConnectionKeys {
  PrivateKey: string;
  PublicKey: string;
}

/** Minimum potrzebne do rozpoznania GENERACJI klucza firmy (kształt z IActiveCompanyKey). */
export type ICompanyKeyIdentity = {
  WorkspaceId: string;
  WorkspaceKeyId: string;
};

/**
 * Tożsamość generacji klucza firmy w postaci „<WorkspaceId>:<WorkspaceKeyId>".
 * Zwraca null, gdy którakolwiek część jest pusta — brak klucza to NIE to samo co inny klucz.
 *
 * Bierze tożsamość WPROST, a nie ICompany z CompanyContext. Powód jest konkretny: Provider
 * tego kontekstu nie jest montowany w App.tsx, więc `company` było zawsze null, `hasCompany`
 * zawsze false, a wtyczka NIGDY się nie łączyła — awaria cicha, bo nic nie rzucało.
 */
export const companyKeyIdentity = (
  key: ICompanyKeyIdentity | null | undefined,
): string | null =>
  key?.WorkspaceId && key?.WorkspaceKeyId
    ? `${key.WorkspaceId}:${key.WorkspaceKeyId}`
    : null;

// Generacja klucza firmy, którą wtyczka jest AKTUALNIE połączona (z ostatniego /api/connect).
// Wtyczka trzyma jeden klucz na połączenie, więc po rotacji generacji albo zmianie firmy
// trzeba połączyć się ponownie — samo IsConnected z wtyczki tego nie wykrywa (dalej jest true).
let connectedCompanyKeyIdentity: string | null = null;

export const getConnectedPluginCompanyKeyIdentity = (): string | null =>
  connectedCompanyKeyIdentity;

export const setConnectedPluginCompanyKeyIdentity = (
  identity: string | null,
): void => {
  connectedCompanyKeyIdentity = identity;
};

/**
 * Czy wtyczkę trzeba połączyć ponownie, bo trzyma klucz innej generacji niż aktywna.
 * Utrata klucza firmy (null) po udanym połączeniu też jest nieaktualnością — wtyczka
 * zostałaby wtedy z materiałem, do którego przeglądarka nie ma już dostępu.
 */
export const isPluginConnectionStale = (
  activeKey: ICompanyKeyIdentity | null | undefined,
): boolean => connectedCompanyKeyIdentity !== companyKeyIdentity(activeKey);

/**
 * Klucz PRYWATNY aktywnej generacji firmy z mirrora (utils/companyContentKeys).
 *
 * Mirror trzyma WSZYSTKIE generacje, na które zalogowany ma grant, a wtyczka przyjmuje tylko
 * jeden klucz — bierzemy więc tę generację, którą aktualnie SZYFRUJEMY, dopasowaną po keyId
 * do klucza publicznego z `peekActiveCompanyKey`. Gdy dopasowanie się nie uda (rozjazd
 * primingu), lepszy jest pierwszy klucz firmy niż żaden — to wciąż klucz firmy, nie profilu.
 * Null oznacza „ta sesja nie ma materiału firmy" i przełącza na ścieżkę wsteczną.
 */
const pickActiveCompanyPrivateKey =
  async (): Promise<openpgp.key.Key | null> => {
    const companyKeys = getCompanyPrivateKeys();
    if (!companyKeys.length) return null;

    const active = peekActiveCompanyKey();
    if (!active?.PublicKeyBase64) return companyKeys[0];

    try {
      // Porównanie po ODCISKU (getFingerprint zwraca hex-string w openpgp 4) — getKeyId()
      // oddaje w runtime obiekt Keyid, którego typy tej wersji opisują jako string.
      const activePublic = await readKeyMaterial(active.PublicKeyBase64);
      const activeFingerprint = activePublic.getFingerprint();
      return (
        companyKeys.find((key) => key.getFingerprint() === activeFingerprint) ??
        companyKeys[0]
      );
    } catch {
      return companyKeys[0];
    }
  };

/**
 * Wylicza parę kluczy (prywatny + publiczny) w formacie oczekiwanym przez wtyczkę:
 * base64(armored) dla prywatnego, base64 packetlistu dla publicznego.
 *
 * Pierwszeństwo ma klucz FIRMY (nim owinięte są klucze obiektów od F3). Ścieżka wsteczna —
 * klucz intermediate — zostaje wyłącznie dla kont bez wydanego jeszcze grantu (D2: konto
 * może istnieć bez firmy, albo czeka na grant).
 */
export const resolvePluginConnectionKeys = async (
  keys: IDecryptedKeys,
): Promise<IPluginConnectionKeys> => {
  const companyKey = await pickActiveCompanyPrivateKey();

  if (companyKey) {
    const PublicKey = await getCompanyPublicKeyBase64().catch(
      () => keys.publicBase64Key!,
    );
    return { PrivateKey: btoa(companyKey.armor()), PublicKey };
  }

  return {
    PrivateKey: keys.intermidatePrivateKey!.data as string,
    PublicKey: keys.publicBase64Key!,
  };
};
